import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { v4 } from 'uuid';
import { v4 as uuidv4 } from 'uuid';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocaleService } from 'src/app/services/locale';
import { VideoService } from 'src/app/services/video';
import { InputComponent } from '../shared/input';
import { DropdownComponent } from '../shared/dropdown';
import { ButtonComponent } from '../shared/button';
import { PaymentService } from 'src/app/services/payment';
import { VideoComponent } from '../shared/video';
import { PlansComponent } from '../shared/plans';
import { UserService } from 'src/app/services/user';
import { environment } from 'src/environments/environment';
import { fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';

@Component({
  selector: 'app-setup',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    CommonModule,
    InputComponent,
    DropdownComponent,
    ButtonComponent,
    VideoComponent,
    PlansComponent,
  ],
  template: `
    <div class="flex flex-col gap-4 mb-8">
      <div class="w-full grid grid-cols-3 gap-4 text-[#183A25]">
        <div
          (click)="currentStep = 1"
          class="flex flex-col gap-4 cursor-pointer"
          [ngClass]="{
            'opacity-50': currentStep === 1,
            'opacity-100': currentStep === 2,
          }"
        >
          <div class="w-full h-1 bg-[#183A25]"></div>
          <div class="font-bold text-lg">
            {{ locale.inform_step_1.heading }}
          </div>
          <div class="font-bold text-[#616161] -mt-4 ">
            {{ locale.inform_step_1.subheading }}
          </div>
        </div>
        <div
          (click)="currentStep = 2"
          class="flex flex-col gap-4 cursor-pointer"
          [ngClass]="{
            'opacity-25': currentStep === 1,
            'opacity-50': currentStep === 2,
            'opacity-100': currentStep === 3,
          }"
        >
          <div class="w-full h-1 bg-[#183A25]"></div>
          <div class="font-bold text-lg">
            {{ locale.inform_step_2.heading }}
          </div>
          <div class="font-bold text-[#616161] -mt-4 ">
            {{ locale.inform_step_2.subheading }}
          </div>
        </div>
        <div
          class="flex flex-col gap-4"
          [ngClass]="{
            'opacity-10': currentStep >= 1,
            'opacity-50': currentStep === 3 || currentStep === 4,
          }"
        >
          <div class="w-full h-1 bg-[#183A25]"></div>
          <div class="font-bold text-lg">
            {{ locale.inform_step_3.heading }}
          </div>
          <div class="font-bold text-[#616161] -mt-4 ">
            {{ locale.inform_step_3.subheading }}
          </div>
        </div>
      </div>
    </div>
    <form class="flex flex-col gap-4" [formGroup]="form">
      <div *ngIf="currentStep === 1" class="grid grid-cols-2 gap-4">
        <app-input
          label="{{ locale.common.lbl_name }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['name']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_surname }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['surname']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_phone_number }}"
          type="number"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['phoneNumber']"
        ></app-input>
        <app-dropdown
          [options]="[
            {
              label: locale.register.usertype_option_company,
              value: 'company',
            },
            {
              label: locale.register.usertype_option_consultant,
              value: 'consultant',
            },
            {
              label: locale.register.usertype_option_site_director,
              value: 'site_director',
            },
            {
              label: locale.register.usertype_option_environmental_manager,
              value: 'environmental_manager',
            },
            {
              label: locale.register.usertype_option_hse_officer,
              value: 'hse_officer',
            },
            {
              label: locale.register.usertype_option_production_supervisor,
              value: 'production_supervisor',
            },
            {
              label: locale.register.usertype_option_quality_manager,
              value: 'quality_manager',
            },
            {
              label: locale.register.usertype_option_maintenance_lead,
              value: 'maintenance_lead',
            },
            {
              label: locale.register.usertype_option_supply_chain_coordinator,
              value: 'supply_chain_coordinator',
            },
          ]"
          label="{{ locale.common.lbl_company_role }}"
          placeholder="{{ locale.common.placeholder_options }}"
          [control]="form.controls['companyRole']"
        ></app-dropdown>
        <div *ngIf="error.length" class="text-red-500">
          {{ error }}
        </div>
      </div>
      <div *ngIf="currentStep === 2" class="grid grid-cols-2 gap-4">
        <app-input
          label="{{ locale.common.lbl_company_name }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['companyName']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_vat_number }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['vatNumber']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_pec }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['pec']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_street }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['street']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_province }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['province']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_postal_code }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['postalCode']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_city }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['city']"
        ></app-input>
        <app-input
          label="{{ locale.common.lbl_sdi_code }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['sdiCode']"
        ></app-input>
        <app-dropdown
          [options]="[
            {
              label: locale.common.business_sector_option_commerce,
              value: locale.common.business_sector_option_commerce,
            },
            {
              label: locale.common.business_sector_option_services,
              value: locale.common.business_sector_option_services,
            },
            {
              label: locale.common.business_sector_option_industry,
              value: locale.common.business_sector_option_industry,
            },
            {
              label: locale.common.business_sector_option_manufacturing,
              value: locale.common.business_sector_option_manufacturing,
            },
            {
              label: locale.common.business_sector_option_craft_artisanal,
              value: locale.common.business_sector_option_craft_artisanal,
            },
            {
              label: locale.common.business_sector_option_agriculture,
              value: locale.common.business_sector_option_agriculture,
            },
            {
              label: locale.common.business_sector_option_construction,
              value: locale.common.business_sector_option_construction,
            },
            {
              label: locale.common.business_sector_option_transport_logistics,
              value: locale.common.business_sector_option_transport_logistics,
            },
            {
              label: locale.common.business_sector_option_energy_utilities,
              value: locale.common.business_sector_option_energy_utilities,
            },
            {
              label: locale.common.business_sector_option_ict_software,
              value: locale.common.business_sector_option_ict_software,
            },
            {
              label: locale.common.business_sector_option_finance_insurance,
              value: locale.common.business_sector_option_finance_insurance,
            },
            {
              label:
                locale.common
                  .business_sector_option_healthcare_personal_services,
              value:
                locale.common
                  .business_sector_option_healthcare_personal_services,
            },
            {
              label: locale.common.business_sector_option_education_training,
              value: locale.common.business_sector_option_education_training,
            },
            {
              label: locale.common.business_sector_option_tourism_hospitality,
              value: locale.common.business_sector_option_tourism_hospitality,
            },
            {
              label: locale.common.business_sector_option_real_estate,
              value: locale.common.business_sector_option_real_estate,
            },
            {
              label: locale.common.business_sector_option_public_administration,
              value: locale.common.business_sector_option_public_administration,
            },
            {
              label: locale.common.business_sector_option_other,
              value: locale.common.business_sector_option_other,
            },
          ]"
          label="{{ locale.common.lbl_business_sector }}"
          placeholder="{{ locale.common.placeholder_options }}"
          [control]="form.controls['businessSector']"
        ></app-dropdown>
        <app-input
          type="number"
          label="{{ locale.common.lbl_no_of_employees }}"
          [keyUp]="reset"
          placeholder="{{ locale.common.placeholder_write_here }}"
          [control]="form.controls['numberOfEmployees']"
        ></app-input>
        <div *ngIf="error.length" class="text-red-500">
          {{ error }}
        </div>
      </div>
      <div *ngIf="currentStep === 3" class="flex flex-col gap-4">
        <div class="font-bold futura">
          {{ locale.inform_step_3.info_title }}
        </div>
        <div class="text-[#616161]">
          {{ locale.inform_step_3.info_subtitle }}
        </div>
        <div id="videos" class="flex flex-col gap-4 videos p-2">
          <app-video [videos]="videos"></app-video>
        </div>
      </div>
      <div *ngIf="currentStep === 4" class="flex flex-col gap-2">
        <app-plans
          [planType]="currentRole === 'company' ? 'Corporate' : 'Consultant'"
          [selectPlan]="selectPlan"
          [selectedPlan]="selectedPlan"
        ></app-plans>
        <div class="flex gap-4">
          <div
            (click)="skipPlan()"
            class="w-1/2 border-2 border-[#043B22] flex font-semibold justify-center gap-1.5 text-[#043B22] px-2.5 sm:px-3 py-1.5 sm:py-2 text-[12px] sm:text-[12px] rounded-md uppercase tracking-wide cursor-pointer items-center transition-all duration-150 hover:opacity-90 active:scale-[0.98]"
          >
            {{ locale.common.button_skip }}
          </div>
          <div class="w-1/2" (click)="openContractModal()">
            <app-button
              [disabled]="!selectedPlan"
              text="{{ locale.inform_step_3.button_confirm }}"
            ></app-button>
          </div>
        </div>
      </div>
    </form>
    <app-button
      [disabled]="getValidity()"
      class="m-10"
      *ngIf="currentStep <= 3"
      [onClick]="nextStep"
      text="{{ locale.common.button_next }}"
    ></app-button>

    <!-- Contract Acceptance Modal -->
    <div
      *ngIf="showContractModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
      (click)="showContractModal = false"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden transform transition-all max-h-[90vh] flex flex-col"
        (click)="$event.stopPropagation()"
      >
        <!-- Modal Header -->
        <div
          class="bg-gradient-to-r from-[#043B22] to-[#065a35] px-5 py-4 flex-shrink-0"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
            </div>
            <div>
              <h3 class="text-white futura text-base sm:text-lg font-semibold">
                Termini e Condizioni
              </h3>
              <p class="text-white/70 text-xs">
                Si prega di compilare i dati e leggere il contratto
              </p>
            </div>
          </div>
        </div>

        <!-- Scrollable Modal Content -->
        <div class="overflow-y-auto flex-1 px-5 py-5">
          <!-- Contract Form Fields -->
          <p class="text-sm font-medium text-[#043B22] mb-3">
            Informazioni Aziendali
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >Ragione Sociale *</label
              >
              <input
                type="text"
                [formControl]="contractForm.controls.companyName"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                placeholder="Ragione sociale"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >Sede *</label
              >
              <input
                type="text"
                [formControl]="contractForm.controls.address"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                placeholder="Indirizzo sede"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >C.F. / P.IVA *</label
              >
              <input
                type="text"
                [formControl]="contractForm.controls.vatNumber"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                placeholder="Codice fiscale / Partita IVA"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >PEC *</label
              >
              <input
                type="email"
                [formControl]="contractForm.controls.pec"
                class="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                [ngClass]="
                  contractForm.controls.pec.touched &&
                  contractForm.controls.pec.invalid
                    ? 'border-red-400'
                    : 'border-gray-300'
                "
                placeholder="Indirizzo PEC"
              />
              <p
                *ngIf="
                  contractForm.controls.pec.touched &&
                  contractForm.controls.pec.hasError('email')
                "
                class="text-[10px] text-red-500 mt-1"
              >
                Inserire un indirizzo email valido
              </p>
            </div>
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >Rappresentante Legale *</label
              >
              <input
                type="text"
                [formControl]="contractForm.controls.legalRepresentative"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#043B22]/30 focus:border-[#043B22] transition-all"
                placeholder="Nome e cognome"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[#616161] mb-1"
                >Luogo e Data</label
              >
              <input
                type="text"
                [value]="getContractPlaceAndDate()"
                disabled
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-[#616161]"
              />
            </div>
          </div>

          <!-- Dynamic Contract Text -->
          <div
            class="border border-gray-200 rounded-xl p-4 sm:p-6 bg-gray-50/50 mb-5 text-[11px] sm:text-xs leading-relaxed text-[#333]"
          >
            <p
              class="text-center text-[10px] text-[#616161] uppercase tracking-wider mb-1"
            >
              FOREVER BAMBU' SPA SOCIETA' BENEFIT
            </p>
            <p class="text-center text-[10px] text-[#616161] mb-4">
              SS11 Padana Superiore 16/18 20063 Cernusco sul Naviglio (MI)<br />P.iva
              12072470961 | info&#64;foreverbambu.com | +39 02 40031483
            </p>

            <h4 class="text-center font-bold text-sm text-[#043B22] mb-1">
              CONDIZIONI GENERALI DI CONTRATTO
            </h4>
            <h4 class="text-center font-bold text-sm text-[#043B22] mb-4">
              SOFTWARE CARBON FOOTPRINT PRO
            </h4>

            <p class="text-center mb-4">Con la presente scrittura privata,</p>

            <p class="font-semibold mb-2">TRA</p>
            <p class="mb-4">
              FOREVER BAMBU S.P.A. SOCIETA BENEFIT (di seguito denominata
              "FOREVER BAMBU" e/o "Fornitore"), con sede legale in SS11 Padana
              Superiore 16/18 - 20063 Cernusco sul Naviglio (MI), P.IVA
              12072470961, PEC: foreverbambu29&#64;pec.it, legalmente
              rappresentata dall'Amministratore Delegato.
            </p>

            <p class="font-semibold mb-2">E</p>
            <p class="mb-2">La Societa (di seguito denominata "Cliente"):</p>
            <p class="mb-1">
              RAGIONE SOCIALE:
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.companyName.value || '_______________'
                }}</span
              >
            </p>
            <p class="mb-1">
              SEDE:
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.address.value || '_______________'
                }}</span
              >
            </p>
            <p class="mb-1">
              C.F./P.IVA:
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.vatNumber.value || '_______________'
                }}</span
              >
            </p>
            <p class="mb-1">
              PEC:
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.pec.value || '_______________'
                }}</span
              >
            </p>
            <p class="mb-2">
              nella persona del suo legale rappresentante
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.legalRepresentative.value ||
                    '_______________'
                }}</span
              >
            </p>
            <p class="mb-4">
              (di seguito congiuntamente "Parti" e disgiuntamente "Parte").
            </p>

            <hr class="my-4 border-gray-300" />

            <p class="font-bold mb-2">Definizioni</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>
                <strong>"Software"</strong>: il programma informatico denominato
                "Carbon Footprint Pro", sviluppato e di proprieta esclusiva di
                FOREVER BAMBU, fornito in modalita SaaS (Software as a Service)
                e ospitato su infrastruttura cloud.
              </li>
              <li>
                <strong>"Servizio"</strong>: l'accesso, l'uso del Software e
                degli eventuali moduli aggiuntivi, secondo le modalita previste
                nel presente Contratto.
              </li>
              <li>
                <strong>"Cliente"</strong>: il soggetto (persona giuridica o
                professionista) che sottoscrive il presente Contratto e che
                utilizza il Software.
              </li>
              <li>
                <strong>"Credenziali di Accesso"</strong>: combinazione di URL,
                username e password necessarie per accedere al Software.
              </li>
              <li>
                <strong>"Manutenzione Ordinaria"</strong>: interventi
                finalizzati a correggere errori e garantire la continuita del
                servizio.
              </li>
              <li>
                <strong>"Manutenzione Straordinaria"</strong>: interventi
                finalizzati a migliorare funzionalita, sicurezza e performance
                del Software, nonche ad adeguarlo a modifiche normative.
              </li>
              <li>
                <strong>"Assistenza"</strong>: supporto tecnico e applicativo
                fornito da FOREVER BAMBU secondo i termini del presente
                Contratto.
              </li>
              <li>
                <strong>"Ordine"</strong>: il documento sottoscritto dal Cliente
                che definisce il canone, la durata, i moduli acquistati e le
                eventuali configurazioni personalizzate.
              </li>
            </ul>
            <p class="mb-4">
              Le Definizioni descritte costituiscono parte integrante del
              presente Contratto.
            </p>

            <p class="font-bold mb-2">Art. 1 - Oggetto del Contratto</p>
            <p class="mb-4">
              Il presente Contratto disciplina le condizioni di utilizzo del
              Software "Carbon Footprint Pro", fornito da FOREVER BAMBU in
              modalita SaaS. Il Cliente ottiene il diritto non esclusivo, non
              trasferibile e limitato nel tempo ad accedere e utilizzare il
              Software, per le proprie esigenze aziendali, nel rispetto delle
              presenti condizioni. Il Contratto include l'attivazione del
              servizio, la concessione delle Credenziali di Accesso e la
              fornitura della Manutenzione Ordinaria e Straordinaria per tutta
              la durata contrattuale.
            </p>

            <p class="font-bold mb-2">Art. 2 - Modalita di Utilizzo</p>
            <p class="mb-4">
              Il Cliente potra accedere al Software esclusivamente tramite le
              Credenziali di Accesso fornite da FOREVER BAMBU. E vietata
              qualsiasi forma di cessione, sublicenza, copia, reverse
              engineering, decompilazione o utilizzo difforme rispetto alla
              finalita del Software. Il Cliente potra consentire l'uso del
              Software unicamente ai propri dipendenti e collaboratori,
              rimanendo responsabile per il loro operato.
            </p>

            <p class="font-bold mb-2">
              Art. 3 - Diritti e Obblighi del Cliente
            </p>
            <p class="mb-2">
              Il Cliente e tenuto ad utilizzare il Software con la massima
              diligenza, nel rispetto della normativa vigente e delle presenti
              condizioni contrattuali. E fatto divieto di:
            </p>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>utilizzare il Software per finalita illecite;</li>
              <li>
                archiviare, pubblicare o trasmettere contenuti che violino
                diritti di proprieta intellettuale, norme di legge, privacy o
                che siano diffamatori, osceni o lesivi di terzi;
              </li>
              <li>
                trasmettere virus, malware o contenuti che possano compromettere
                la sicurezza del sistema.
              </li>
            </ul>
            <p class="mb-4">
              Il Cliente manleva FOREVER BAMBU da ogni responsabilita per l'uso
              improprio del Software e da eventuali richieste risarcitorie di
              terzi derivanti da tali comportamenti. FOREVER BAMBU potra
              sospendere immediatamente l'accesso in caso di violazioni gravi.
            </p>

            <p class="font-bold mb-2">
              Art. 4 - Corrispettivo e Modalita di Pagamento
            </p>
            <p class="mb-2">
              Il Cliente corrispondera a FOREVER BAMBU un canone di abbonamento
              annuale o mensile, come indicato nell'Ordine. Il pagamento e
              anticipato e costituisce condizione necessaria per l'attivazione
              del servizio.
            </p>
            <p class="mb-2">
              Il canone comprende l'utilizzo del Software, la Manutenzione
              Ordinaria e Straordinaria e l'accesso agli aggiornamenti. Sono
              escluse eventuali attivita di consulenza, formazione,
              personalizzazione o integrazione con sistemi terzi, che saranno
              oggetto di offerte separate.
            </p>
            <p class="mb-2">
              In caso di mancato pagamento entro 30 (trenta) giorni dalla
              scadenza, FOREVER BAMBU potra sospendere l'accesso al Software
              fino alla regolarizzazione.
            </p>
            <p class="mb-4">
              Il canone potra essere aggiornato annualmente in base all'indice
              FOI ISTAT, con preavviso scritto di 60 giorni.
            </p>

            <p class="font-bold mb-2">
              Art. 5 - Manutenzione, Aggiornamenti e Assistenza
            </p>
            <p class="mb-2">
              FOREVER BAMBU garantira la Manutenzione Ordinaria per correggere
              eventuali malfunzionamenti segnalati dal Cliente. La Manutenzione
              Straordinaria, volta a migliorare e aggiornare il Software, sara
              implementata automaticamente e senza costi aggiuntivi.
            </p>
            <p class="font-semibold mb-1">Livelli di Servizio (SLA):</p>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>Disponibilita minima garantita: 99,5% su base annuale.</li>
              <li>
                Tempo di risposta ticket: Alta priorita: 4 ore lavorative; Media
                priorita: 8 ore lavorative; Bassa priorita: 24 ore lavorative.
              </li>
            </ul>
            <p class="font-semibold mb-1">Service Credit:</p>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>
                &lt; 99,5% ma &ge; 99%: credito pari al 10% del canone annuo.
              </li>
              <li>
                &lt; 99% ma &ge; 98%: credito pari al 25% del canone annuo.
              </li>
              <li>&lt; 98%: credito pari al 50% del canone annuo.</li>
            </ul>
            <p class="mb-4">
              Il Cliente dovra fornire informazioni dettagliate sulle anomalie
              per consentire diagnosi e risoluzione tempestiva.
            </p>

            <p class="font-bold mb-2">Art. 6 - Durata e Rinnovo</p>
            <p class="mb-2">
              Il presente Contratto ha durata minima di 12 (dodici) mesi dalla
              data di attivazione del Software. Alla scadenza si rinnova
              tacitamente per ulteriori 12 mesi, salvo disdetta scritta tramite
              PEC almeno 90 (novanta) giorni prima della scadenza.
            </p>
            <p class="mb-4">
              Il Cliente puo recedere senza penali entro 30 giorni dalla
              ricezione di modifiche sostanziali a prezzo, SLA o condizioni
              contrattuali.
            </p>

            <p class="font-bold mb-2">Art. 7 - Sospensione e Risoluzione</p>
            <p class="mb-2">
              FOREVER BAMBU potra sospendere il servizio in caso di mancato
              pagamento, di gravi violazioni contrattuali o per motivi di
              sicurezza. La sospensione sara comunicata via PEC con indicazione
              dei tempi di riattivazione.
            </p>
            <p class="mb-4">
              In caso di risoluzione, il Cliente dovra esportare i propri dati
              entro 30 (trenta) giorni; successivamente FOREVER BAMBU provvedera
              alla cancellazione definitiva di dati e backup.
            </p>

            <p class="font-bold mb-2">Art. 8 - Proprieta Intellettuale</p>
            <p class="mb-4">
              Il Software, i codici sorgente e ogni diritto di proprieta
              intellettuale restano di esclusiva titolarita di FOREVER BAMBU. Il
              Cliente acquisisce solo un diritto d'uso limitato, personale e non
              trasferibile.
            </p>

            <p class="font-bold mb-2">Art. 9 - Input e Output</p>
            <p class="mb-2">
              Il Cliente mantiene la titolarita sugli Input (dati caricati) e
              sugli Output generati dal Software. FOREVER BAMBU potra utilizzare
              dati anonimizzati per finalita statistiche e di miglioramento del
              servizio, senza possibilita di identificazione del Cliente.
            </p>
            <p class="mb-4">
              Gli Output hanno finalita informativa e non costituiscono
              consulenza professionale vincolante.
            </p>

            <p class="font-bold mb-2">Art. 10 - Responsabilita e Indennizzo</p>
            <p class="mb-2">
              La responsabilita complessiva di FOREVER BAMBU e limitata al 50%
              del canone annuo corrisposto, salvo dolo o colpa grave.
            </p>
            <p class="mb-2">
              Il Cliente manleva FOREVER BAMBU per danni derivanti da uso
              improprio del Software o da violazione di diritti di terzi.
            </p>
            <p class="mb-4">
              FOREVER BAMBU manlevera il Cliente per eventuali azioni di terzi
              per violazione di diritti IP derivanti dall'uso conforme del
              Software.
            </p>

            <p class="font-bold mb-2">
              Art. 11 - Protezione dei Dati e Sicurezza
            </p>
            <p class="mb-2">
              FOREVER BAMBU trattera i dati personali in qualita di responsabile
              ai sensi del GDPR.
            </p>
            <p class="mb-2">
              In caso di data breach, FOREVER BAMBU notifichera il Cliente entro
              24 ore dalla scoperta per consentire l'adempimento dell'obbligo di
              notifica entro 72 ore previsto dal GDPR.
            </p>
            <p class="mb-4">
              Sono adottate misure tecniche e organizzative adeguate:
              crittografia, backup giornaliero con retention di 14 giorni,
              logging degli accessi e segregazione dei dati per cliente.
            </p>

            <p class="font-bold mb-2">Art. 12 - Subprocessor</p>
            <p class="mb-4">
              FOREVER BAMBU potra avvalersi di subprocessor per l'erogazione del
              servizio (es. provider cloud). L'elenco aggiornato e disponibile
              su richiesta. FOREVER BAMBU notifichera modifiche sostanziali
              all'elenco con congruo preavviso e rimarra responsabile per le
              attivita dei subprocessor.
            </p>

            <p class="font-bold mb-2">Art. 13 - Audit e Conformita</p>
            <p class="mb-4">
              Il Cliente potra richiedere audit di sicurezza previo preavviso e
              in accordo con FOREVER BAMBU, senza compromettere la sicurezza e
              la riservatezza di altri clienti.
            </p>

            <p class="font-bold mb-2">Art. 14 - Beta e Servizi in Preview</p>
            <p class="mb-4">
              Funzionalita Beta o in Preview saranno fornite "as is" e potranno
              essere escluse da SLA e indennizzi.
            </p>

            <p class="font-bold mb-2">
              Art. 15 - Legge Applicabile e Foro Competente
            </p>
            <p class="mb-4">
              Il presente Contratto e regolato dalla legge italiana. Per
              qualsiasi controversia e competente in via esclusiva il Foro di
              Milano.
            </p>

            <hr class="my-4 border-gray-300" />

            <p class="mb-2">
              Luogo e Data:
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{ getContractPlaceAndDate() }}</span
              >
            </p>
            <p class="mb-4">Per accettazione</p>
            <p class="mb-2">
              Il Cliente
              <span
                class="font-semibold text-[#043B22] border-b border-[#043B22]/30 px-1"
                >{{
                  contractForm.controls.companyName.value || '_______________'
                }}</span
              >
              (Timbro e Firma)
            </p>
            <p>
              FOREVER BAMBU S.P.A. SOCIETA BENEFIT ___________________________
              (Firma)
            </p>
          </div>

          <!-- Checkbox -->
          <label
            class="flex items-start gap-3 cursor-pointer select-none"
            (click)="contractAccepted = !contractAccepted"
          >
            <div
              class="w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
              [ngClass]="
                contractAccepted
                  ? 'bg-[#043B22] border-[#043B22]'
                  : 'border-gray-300 hover:border-[#043B22]'
              "
            >
              <svg
                *ngIf="contractAccepted"
                class="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <span class="text-sm text-[#333]"
              >Ho letto e accetto le Condizioni Generali di Contratto</span
            >
          </label>
        </div>

        <!-- Modal Actions -->
        <div
          class="px-5 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0"
        >
          <button
            (click)="showContractModal = false"
            class="flex-1 px-4 py-2.5 text-sm font-medium text-[#616161] bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer"
          >
            Annulla
          </button>
          <button
            (click)="confirmContract()"
            [disabled]="!contractAccepted || contractForm.invalid"
            class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all flex items-center justify-center gap-2"
            [ngClass]="
              contractAccepted && contractForm.valid
                ? 'bg-[#043B22] hover:bg-[#065a35] cursor-pointer shadow-md'
                : 'bg-gray-300 cursor-not-allowed'
            "
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              ></path>
            </svg>
            Procedi al Pagamento
          </button>
        </div>
      </div>
    </div>
  `,
})
export class SetupComponent {
  private user = inject(UserService);
  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);

  videos = inject(VideoService).getVideos(2);
  locale = inject(LocaleService).locale();

  error = '';
  currentStep = 1;
  currentRole: any = null;
  selectedPlan: any = '';
  showContractModal = false;
  contractAccepted = false;
  contractForm = new FormGroup({
    companyName: new FormControl('', Validators.required),
    address: new FormControl('', Validators.required),
    vatNumber: new FormControl('', Validators.required),
    pec: new FormControl('', [Validators.required, Validators.email]),
    legalRepresentative: new FormControl('', Validators.required),
  });

  form = new FormGroup({
    id: new FormControl(this.generateId()),
    name: new FormControl('', Validators.required),
    surname: new FormControl('', Validators.required),
    phoneNumber: new FormControl('', [
      Validators.minLength(10),
      Validators.maxLength(10),
    ]),
    companyRole: new FormControl('', Validators.required),
    companyName: new FormControl('', Validators.required),
    vatNumber: new FormControl('', Validators.required),
    street: new FormControl(''),
    postalCode: new FormControl(''),
    city: new FormControl(''),
    province: new FormControl(''),
    country: new FormControl('Italia'),
    businessSector: new FormControl(''),
    numberOfEmployees: new FormControl(''),
    profilePhoto: new FormControl(''),
    pec: new FormControl('', Validators.email),
    legalRepresentative: new FormControl(''),
    sdiCode: new FormControl(''),
  });

  getValidity = () => {
    let nameValid = false;
    let surnameValid = false;
    let companyRoleValid = false;

    nameValid = this.form.controls.name.value
      ? this.form.controls.name.value.length < 0
      : true;
    surnameValid = this.form.controls.surname.value
      ? this.form.controls.surname.value.length < 0
      : true;
    companyRoleValid = this.form.controls.companyRole.value
      ? this.form.controls.companyRole.value.length < 0
      : true;

    return nameValid || surnameValid || companyRoleValid;
  };

  nextStep = async () => {
    this.currentStep += 1;
    this.currentRole = this.form.controls.companyRole.value;
  };

  skipPlan = async () => {
    try {
      this.spinner.show();
      this.user.createUser(this.form);
      this.spinner.hide();
      this.router.navigateByUrl('/dashboard/calculations');
    } catch (error) {
      this.spinner.hide();
      this.error = (error as any).message;
    }
  };

  reset = () => {
    this.error = '';
  };

  selectPlan = async (plan: any) => {
    this.selectedPlan = plan;
  };

  openContractModal = () => {
    if (!this.selectedPlan) return;
    this.contractAccepted = false;
    const street = this.form.controls.street.value ?? '';
    const postalCode = this.form.controls.postalCode.value ?? '';
    const city = this.form.controls.city.value ?? '';
    const province = this.form.controls.province.value ?? '';
    const addressParts = [
      street,
      [postalCode, city].filter(Boolean).join(' '),
      province ? `(${province})` : '',
    ].filter(Boolean);
    this.contractForm.patchValue({
      companyName: this.form.controls.companyName.value ?? '',
      address: addressParts.join(', '),
      vatNumber: this.form.controls.vatNumber.value ?? '',
      pec: this.form.controls.pec.value ?? '',
      legalRepresentative: '',
    });
    this.showContractModal = true;
  };

  confirmContract = () => {
    if (!this.contractAccepted || this.contractForm.invalid) return;
    this.showContractModal = false;
    // Sync contract form values back to the main form fields
    this.form.controls.companyName.setValue(
      this.contractForm.controls.companyName.value ?? '',
    );
    this.form.controls.vatNumber.setValue(
      this.contractForm.controls.vatNumber.value ?? '',
    );
    this.form.controls.pec.setValue(this.contractForm.controls.pec.value ?? '');
    this.form.controls.legalRepresentative.setValue(
      this.contractForm.controls.legalRepresentative.value ?? '',
    );
    // Split address fields from step 2 remain unchanged
    this.initiatePayment();
  };

  getContractPlaceAndDate(): string {
    const city = this.form.controls.city.value || '';
    const now = new Date();
    const date = now.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return city ? city + ', ' + date : date;
  }

  initiatePayment = async () => {
    try {
      this.spinner.show();
      await this.user.createUser(this.form);

      const userEmail = (await fetchUserAttributes()).email;
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        this.spinner.hide();
        return;
      }

      const response = await fetch(environment.lambdaUrls.stripe, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'create_checkout_session',
          priceId: this.selectedPlan.id,
          successUrl: `${environment.baseUrl}/dashboard/profile?subscribed=${this.selectedPlan.id}&id=${this.form.controls.id.value}`,
          cancelUrl: `${environment.baseUrl}/dashboard/profile?subscribed=n`,
        }),
      });

      const result = await response.json();

      if (response.ok && result.url) {
        window.location.href = result.url;
      }

      this.spinner.hide();
    } catch (error) {
      this.spinner.hide();
    }
  };
  private generateId(): string {
    try {
      return uuidv4();
    } catch (e) {
      return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
  }
}
